// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { ArchiveControl } from '@/components/archive-control';
import { IntegrationStatus } from '@/components/integration-status';
import { Link } from '@/components/link';
import { NotFoundErrorBoundary } from '@/components/not-found-error-boundary';
import { OverflowTypography } from '@/components/overflow-typography';
import { SupportErrorBoundary } from '@/components/support-error-boundary';
import { VerticalNavigationLink } from '@/components/vertical-navigation-link';
import { EditRounded, MergeRounded } from '@/constants/hardfin-standard-icons';
import { useDisclosure } from '@/hooks/use-disclosure';
import { useFeatureFlags } from '@/hooks/use-feature-flags';
import { getItem } from '@/items/api/get-item';
import { updateItem } from '@/items/api/update-item';
import { EditItemDialog } from '@/items/components/edit-item-dialog';
import { ItemTypeChip } from '@/items/components/item-type-chip';
import { MergeItemDialog } from '@/items/components/merge-item-dialog';
import {
  Box,
  Breadcrumbs,
  Chip,
  Container,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  useMutation,
  useQueryClient,
  useSuspenseQuery,
} from '@tanstack/react-query';
import { Outlet, createFileRoute } from '@tanstack/react-router';
import { Suspense } from 'react';

/**
 * Route configuration for the item detail page.
 * This route handles displaying and managing a single item's details.
 * The route parameter 'itemKey' is used to identify the specific item.
 */
export const Route = createFileRoute('/_app/catalog/items/$itemKey')({
  component: ItemRoute,
});

function ItemRoute() {
  const { itemKey } = Route.useParams();

  return (
    <NotFoundErrorBoundary
      entityType="item"
      listPath="/catalog/items"
      listLabel="Go to items"
    >
      <ItemContent itemKey={itemKey} />
    </NotFoundErrorBoundary>
  );
}

/**
 * Item detail page component.
 *
 * Features:
 * - Displays item name, type, SKU, and archive status
 * - Allows editing of item details via dialog
 * - Provides navigation to conditional tabs based on item type
 * - Supports archiving/unarchiving items
 *
 * Tab visibility by item type:
 * - Fields: DEVICE only (hasAssets)
 * - Accounting: DEVICE only (hasAssets)
 * - Pricing: SERVICE, DEVICE, and BULK
 * - BOM: DEVICE only with enableBOM feature flag (hasAssets && enableBOM)
 * - Related assets: DEVICE only (hasAssets)
 * - Audit: SERVICE and DEVICE only (!isPhysical || hasAssets)
 */
function ItemContent(props: { itemKey: string }) {
  const queryClient = useQueryClient();
  const featureFlags = useFeatureFlags();

  const { data } = useSuspenseQuery({
    queryKey: ['item', props.itemKey],
    queryFn: () => getItem(props.itemKey),
  });

  const archiveItemMutation = useMutation({
    mutationFn: (isArchived: boolean) => {
      return updateItem(data.item.key, { isArchived });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['item'] });
    },
  });

  const editDialog = useDisclosure();
  const mergeDialog = useDisclosure();

  const archiveToggleTooltip = data.item.isArchived
    ? 'Unarchive item'
    : 'Archive item. Archiving does not change your item, but archived items are hidden by default in the item list.';

  const handleChangeArchived = (isArchived: boolean) => {
    archiveItemMutation.mutate(isArchived);
  };

  // Tab visibility logic
  const showFieldsTab = data.item.hasAssets;
  const showInventoryTab = data.item.isPhysical && !data.item.hasAssets;
  const showMovementTab = data.item.isPhysical && !data.item.hasAssets;
  const showAccountingTab = data.item.hasAssets;
  const showBOMTab = data.item.hasAssets && featureFlags.enableBOM;
  const showAssetsTab = data.item.hasAssets;
  const showAuditTab = !data.item.isPhysical || data.item.hasAssets;

  return (
    <Container maxWidth="xl">
      <EditItemDialog
        isOpen={editDialog.isOpen}
        item={data.item}
        onClose={editDialog.close}
      />
      {data.item.type !== 'BULK' && (
        <MergeItemDialog
          isOpen={mergeDialog.isOpen}
          item={data.item}
          onClose={mergeDialog.close}
        />
      )}
      <Stack marginY={3} spacing={3} paddingX={{ xs: 1, md: 3 }}>
        <Stack>
          <Breadcrumbs separator="›">
            <Link
              to="/catalog/items"
              variant="body2"
              color="text.secondary"
              sx={{ display: 'block' }}
            >
              Items
            </Link>
            <Typography display="flex" variant="body2" color="text.secondary">
              {data.item.name}
            </Typography>
          </Breadcrumbs>
          <Stack
            useFlexGap
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            marginBottom={1}
          >
            <Stack useFlexGap direction="row" alignItems="center" spacing={1}>
              <IntegrationStatus status={data.item.integrations} />
              <Typography variant="h5">
                {data.item.sku}
                <Typography
                  component="span"
                  variant="h5"
                  color="text.secondary"
                  marginLeft={1}
                >
                  ({data.item.name})
                </Typography>
              </Typography>
              {data.item.isArchived && <Chip size="small" label="Archived" />}
            </Stack>
            <Stack direction="row" spacing={1}>
              <Tooltip arrow title="Edit item">
                <IconButton size="small" onClick={editDialog.open}>
                  <EditRounded fontSize="small" />
                </IconButton>
              </Tooltip>
              {data.item.type !== 'BULK' && (
                <Tooltip arrow title="Merge with another item">
                  <IconButton size="small" onClick={mergeDialog.open}>
                    <MergeRounded fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip arrow placement="top" title={archiveToggleTooltip}>
                <span>
                  <ArchiveControl
                    isArchived={data.item.isArchived}
                    isDisabled={archiveItemMutation.isPending}
                    onChange={handleChangeArchived}
                  />
                </span>
              </Tooltip>
            </Stack>
          </Stack>
          <Stack spacing={1} direction="row" alignItems="center">
            <Typography variant="subtitle2">Type</Typography>
            <ItemTypeChip item={data.item} />
          </Stack>
          <Stack spacing={1} direction="row" alignItems="center" marginTop={1}>
            <Typography variant="subtitle2">Description</Typography>
            <OverflowTypography
              variant="body2"
              color="text.secondary"
              maxWidth="30rem"
              overflow="hidden"
              whiteSpace="nowrap"
              textOverflow="ellipsis"
            >
              {data.item.description ?? 'Not set'}
            </OverflowTypography>
          </Stack>
        </Stack>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <Stack
            direction={{ xs: 'row', md: 'column' }}
            borderTop="1px solid transparent"
          >
            {showFieldsTab && (
              <VerticalNavigationLink
                to="/catalog/items/$itemKey"
                params={{ itemKey: props.itemKey }}
                activeOptions={{ exact: true }}
              >
                Fields
              </VerticalNavigationLink>
            )}
            {showInventoryTab && (
              <VerticalNavigationLink
                to="/catalog/items/$itemKey/units"
                params={{ itemKey: props.itemKey }}
              >
                Units on location
              </VerticalNavigationLink>
            )}
            {showMovementTab && (
              <VerticalNavigationLink
                to="/catalog/items/$itemKey/movement"
                params={{ itemKey: props.itemKey }}
              >
                Movement history
              </VerticalNavigationLink>
            )}
            {showAccountingTab && (
              <VerticalNavigationLink
                to="/catalog/items/$itemKey/accounting"
                params={{ itemKey: props.itemKey }}
              >
                Accounting
              </VerticalNavigationLink>
            )}
            <VerticalNavigationLink
              to="/catalog/items/$itemKey/pricing"
              params={{ itemKey: props.itemKey }}
            >
              Pricing
            </VerticalNavigationLink>
            {showBOMTab && (
              <VerticalNavigationLink
                to="/catalog/items/$itemKey/bom"
                params={{ itemKey: props.itemKey }}
              >
                Bill of materials
              </VerticalNavigationLink>
            )}
            {showAssetsTab && (
              <VerticalNavigationLink
                to="/catalog/items/$itemKey/assets"
                params={{ itemKey: props.itemKey }}
              >
                Related assets
              </VerticalNavigationLink>
            )}
            {showAuditTab && (
              <VerticalNavigationLink
                to="/catalog/items/$itemKey/audit"
                params={{ itemKey: props.itemKey }}
              >
                Audit log
              </VerticalNavigationLink>
            )}
          </Stack>
          <Box flexGrow={1} paddingLeft={{ xs: 0, md: 2 }}>
            <SupportErrorBoundary message="An error occurred.">
              <Suspense>
                <Outlet />
              </Suspense>
            </SupportErrorBoundary>
          </Box>
        </Stack>
      </Stack>
    </Container>
  );
}
